import { CANCELLED_BY_SEND, isUnansweredApproval } from '@/lib/constants/tool-outcomes';

import type { ToolCallMessagePart } from '@assistant-ui/react';

export interface ToolStatus {
  type: 'running' | 'complete' | 'incomplete' | 'requires-action';
  reason?: string;
}

export function getToolStatusFlags(status?: ToolStatus) {
  return {
    isRunning: status?.type === 'running',
    isComplete: status?.type === 'complete',
    isError: status?.type === 'incomplete' && status?.reason === 'error',
  };
}

/** A runtime's verdict on an approval nobody answered: assistant-ui's own union. */
export type ApprovalResolution = NonNullable<ToolCallMessagePart['approval']>['resolution'];

export interface ToolCallApproval {
  approved?: boolean;
  reason?: string;
  resolution?: ApprovalResolution;
}

// Message-wide status conflates running, declined and queued siblings. Prefer each call's approval and result.
export function buildToolCallStatus({
  result,
  hasError,
  approval,
  isGated,
}: {
  result: unknown;
  hasError?: boolean;
  approval?: ToolCallApproval;
  isGated?: boolean;
}): ToolStatus {
  if (approval?.approved === false && approval.reason === CANCELLED_BY_SEND)
    return { type: 'incomplete', reason: 'cancelled' };
  if (approval?.resolution) return { type: 'incomplete', reason: approval.resolution };
  if (isUnansweredApproval(approval)) return { type: 'requires-action', reason: 'unanswered' };
  if (approval?.approved === false) return { type: 'incomplete', reason: 'declined' };
  if (result !== undefined)
    return buildToolStatusFromFlags({ isRunning: false, hasError: !!hasError });
  if (!approval) {
    return isGated ? { type: 'requires-action', reason: 'queued' } : { type: 'running' };
  }
  if (approval.approved === true) return { type: 'running' };
  return { type: 'requires-action' };
}

export function buildToolStatusFromFlags({
  isRunning,
  hasError,
}: {
  isRunning: boolean;
  hasError: boolean;
}): ToolStatus {
  if (isRunning) return { type: 'running' };
  if (hasError) return { type: 'incomplete', reason: 'error' };
  return { type: 'complete' };
}
